import { format } from 'date-fns';
import { FC, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { IconProps } from '../../../assets/icons/icon.types';
import {
  AlertCircleIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  DividendsIcon,
} from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { Calendar } from '../../../components/calendar/index';
import { ModalWrapper } from '../../../components/modal-wrapper/index';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/popover/index';
import { DividendPayoutStatus } from '../../../hooks/dividend/dividend.types';
import { DividendHooks } from '../../../hooks/index';
import { selectedCompanyStorage } from '../../../storage/company/index';
import { AccessRole } from '../../../types/stakeholder.types';
import { getTranslation } from '../../../utils/get-translation.util';

type Props = {
  approvalDate?: null | string;
  companyId: string;
  payoutDate?: null | string;
  payoutId: string;
  status: DividendPayoutStatus;
};

type StatusConfig = {
  icon: FC<IconProps>;
  iconColor: string;
  label: string;
  textClass: string;
};

const statusConfig: Record<DividendPayoutStatus, StatusConfig> = {
  [DividendPayoutStatus.APPROVED]: {
    icon: CheckCircleIcon,
    iconColor: '#2565C8',
    label: getTranslation('DIVIDEND_STATUS_APPROVED'),
    textClass: 'text-brand-700',
  },
  [DividendPayoutStatus.PAID_OUT]: {
    icon: DividendsIcon,
    iconColor: '#039855',
    label: getTranslation('DIVIDEND_STATUS_PAID_OUT'),
    textClass: 'text-forest-600',
  },
  [DividendPayoutStatus.PENDING]: {
    icon: AlertCircleIcon,
    iconColor: '#B54708',
    label: getTranslation('DIVIDEND_STATUS_PENDING'),
    textClass: 'text-sunbeam-700',
  },
};

const allowedTransitions: Record<DividendPayoutStatus, DividendPayoutStatus[]> = {
  [DividendPayoutStatus.PENDING]: [DividendPayoutStatus.APPROVED, DividendPayoutStatus.PAID_OUT],
  [DividendPayoutStatus.APPROVED]: [DividendPayoutStatus.PAID_OUT, DividendPayoutStatus.PENDING],
  [DividendPayoutStatus.PAID_OUT]: [DividendPayoutStatus.APPROVED, DividendPayoutStatus.PENDING],
};

const statusesWithDate = new Set([DividendPayoutStatus.APPROVED, DividendPayoutStatus.PAID_OUT]);

const dateModalTitle: Record<DividendPayoutStatus, string> = {
  [DividendPayoutStatus.APPROVED]: getTranslation('APPROVAL_DATE'),
  [DividendPayoutStatus.PAID_OUT]: getTranslation('PAYOUT_DATE'),
  [DividendPayoutStatus.PENDING]: '',
};

export const StatusLabel: FC<{ status: DividendPayoutStatus }> = ({ status }) => {
  const { icon: Icon, iconColor, label, textClass } = statusConfig[status];

  return (
    <span className="flex items-center gap-1">
      <Icon className="size-4" iconColor={iconColor} />
      <span className={twMerge('text-xs font-medium', textClass)}>{label}</span>
    </span>
  );
};

export const DividendStatusBadge: FC<Props> = ({ approvalDate, payoutDate, payoutId, status }) => {
  const [open, setOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<DividendPayoutStatus | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());

  const isAuditor = selectedCompanyStorage(
    ({ company }) => company?.stakeholder?.accessRole === AccessRole.AUDITOR,
  );

  const { transition, isPending } = DividendHooks.useDividendTransitionStatus();

  const handleSelectStatus = (next: DividendPayoutStatus) => {
    // Reverting Paid Out → Approved keeps the original approval date (payout date is cleared
    // server-side), so no date picker is needed — transition immediately.
    const skipDatePicker =
      !statusesWithDate.has(next) ||
      (status === DividendPayoutStatus.PAID_OUT && next === DividendPayoutStatus.APPROVED);

    if (skipDatePicker) {
      transition({ payoutId, status: next });
      setOpen(false);
      setPendingStatus(null);
      return;
    }
    setOpen(false);
    setPendingStatus(next);
    setSelectedDate(new Date());
  };

  const handleConfirm = () => {
    if (!pendingStatus) return;
    transition({
      date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined,
      payoutId,
      status: pendingStatus,
    });
    setOpen(false);
    setPendingStatus(null);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setPendingStatus(null);
  };

  const nextStatuses = allowedTransitions[status];

  if (isAuditor) {
    return (
      <span className="flex w-[120px] items-center justify-center gap-2 rounded border border-gray-100 bg-white px-3 py-1.5 shadow-sm">
        <StatusLabel status={status} />
      </span>
    );
  }

  const getPickerDisabled = () => {
    if (pendingStatus === DividendPayoutStatus.PAID_OUT && approvalDate)
      return { before: new Date(approvalDate) };
    if (pendingStatus === DividendPayoutStatus.APPROVED && payoutDate)
      return { after: new Date(payoutDate) };
    return undefined;
  };

  return (
    <>
      <Popover onOpenChange={handleOpenChange} open={open}>
        <PopoverTrigger asChild>
          <button
            className="flex w-[120px] cursor-pointer items-center justify-between gap-1 rounded border border-gray-100 bg-white py-1.5 pl-3 pr-2 shadow-sm transition-colors hover:bg-gray-50"
            type="button"
          >
            <StatusLabel status={status} />
            <ChevronDownIcon className="size-4 shrink-0" iconColor="#667085" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          className="z-50 w-[var(--radix-popover-trigger-width)] p-1"
          sideOffset={4}
        >
          <div className="flex flex-col">
            <p className="border-b border-gray-100 px-2 pb-2 pt-1 text-xs font-[450] text-gray-500">
              <AppFormattedMessage id="MARK_AS" />
            </p>
            {nextStatuses.map((next) => {
              const { icon: Icon, iconColor, label } = statusConfig[next];

              return (
                <button
                  className="flex items-center gap-2 rounded px-2 py-2.5 text-left hover:bg-gray-50"
                  key={next}
                  onClick={() => handleSelectStatus(next)}
                  type="button"
                >
                  <Icon className="size-4 shrink-0" iconColor={iconColor} />
                  <span className="text-sm font-[450] text-gray-700">{label}</span>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      {pendingStatus && (
        <ModalWrapper
          className="w-full max-w-[420px]"
          control={{ onOpenChange: (next) => !next && setPendingStatus(null), open: true }}
          onInteractOutside={() => setPendingStatus(null)}
          side="CENTER"
        >
          <div className="flex w-full max-w-[380px] flex-col gap-6 rounded bg-white p-4">
            <span className="text-xl font-semibold text-gray-700">
              {dateModalTitle[pendingStatus]}
            </span>
            <div className="flex justify-center rounded-lg border border-gray-100 p-2">
              <Calendar
                className="w-full px-2 py-1"
                disabled={getPickerDisabled()}
                initialFocus
                mode="single"
                onSelect={setSelectedDate}
                selected={selectedDate}
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                className="flex h-11 items-center rounded border border-gray-300 bg-white px-6 text-sm font-[450] text-gray-700 hover:bg-gray-50"
                onClick={() => setPendingStatus(null)}
                type="button"
              >
                <AppFormattedMessage id="CANCEL" />
              </button>
              <button
                className={twMerge(
                  'flex h-11 items-center rounded bg-brand-700 px-6 text-sm font-[550] text-white',
                  (isPending || !selectedDate) && 'opacity-50',
                )}
                disabled={isPending || !selectedDate}
                onClick={handleConfirm}
                type="button"
              >
                <AppFormattedMessage id={isPending ? 'SAVING_ELLIPSIS' : 'SAVE'} />
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </>
  );
};
